/** Channels whose row is hidden when there is no assignee and no messages yet. */
export type StaffChatChannel = "personal_technician" | "personal_manager";

export type ChatThreadVisibilityRow = {
  channel: "express_support" | "care_department" | StaffChatChannel;
  assignee?: null | unknown;
  last_message: null | unknown;
};

export function isPersonalStaffChannel(channel: ChatThreadVisibilityRow["channel"]): channel is StaffChatChannel {
  return channel === "personal_technician" || channel === "personal_manager";
}

/** Hide thread in list / widget when there is no specialist and the thread was never used. */
export function shouldHideStaffThreadWhenEmpty(t: ChatThreadVisibilityRow): boolean {
  return isPersonalStaffChannel(t.channel) && t.assignee == null && t.last_message == null;
}

/** Block doctor compose (and uploads) when personal technician/manager is not assigned. */
export function isStaffChannelComposeLocked(
  channel: ChatThreadVisibilityRow["channel"],
  assignee: ChatThreadVisibilityRow["assignee"],
): boolean {
  return isPersonalStaffChannel(channel) && assignee == null;
}

export function filterVisibleChatThreads<T extends ChatThreadVisibilityRow>(threads: T[]): T[] {
  return threads.filter((t) => !shouldHideStaffThreadWhenEmpty(t));
}

/** Doctor chat list order: express → technician → manager → care department (4th). */
export const CHAT_CHANNEL_ORDER: readonly ChatThreadVisibilityRow["channel"][] = [
  "express_support",
  "personal_technician",
  "personal_manager",
  "care_department",
];

export function sortChatThreadsByChannelOrder<T extends { channel: ChatThreadVisibilityRow["channel"] }>(
  threads: T[],
): T[] {
  const order = new Map(CHAT_CHANNEL_ORDER.map((channel, index) => [channel, index]));
  return [...threads].sort(
    (a, b) => (order.get(a.channel) ?? 99) - (order.get(b.channel) ?? 99),
  );
}
